import ClaudeService from "./claudeService";
import { findDeskUpdate } from "../lib/deskWrites";

const claimsDeskWrite = (parsed) => parsed?.desk_action === "new_doc" || parsed?.desk_action === "append";

// Lumen's words promise a desk change that is on its way or already done. Offers ("Want me to add
// it?") don't match. Phrases only the desk flow uses count on their own; general ones ("here's a
// draft") count only when the reply is also about the doc, so a draft email written in chat doesn't.
const DESK_ONLY_PROMISE_RE = /update the doc|keep both|swap it in|you'?ll see the option|(?:added|adding) (?:it|that|this|a [\w\s'-]{1,40}) (?:to|at) the (?:bottom|doc|desk)/i;
const GENERAL_PROMISE_RE = /sending (?:a |the |that )?(?:rewrite|version|update|draft|change)|here'?s (?:a|the|that) (?:\w+ ){0,2}(?:draft|version|rewrite)|i(?:'ve| have)? drafted|drafted (?:a|an|the|that)/i;
const MENTIONS_DOC_RE = /\b(?:doc|document|desk)\b/i;

/** True when a reply tells the user a desk change is coming or done, but carries no desk write. */
export function promisesUnsentDeskWrite(parsed, { activeSurface } = {}) {
  if (!parsed || claimsDeskWrite(parsed) || parsed.desk_action === "ask_user") return false;
  if (activeSurface && activeSurface !== "desk") return false; // at the whiteboard etc., "draft" is about that surface
  if ((parsed.surface_updates || []).length) return false;
  const text = String(parsed.chat_response || "");
  return DESK_ONLY_PROMISE_RE.test(text) || (GENERAL_PROMISE_RE.test(text) && MENTIONS_DOC_RE.test(text));
}

async function askAgain(callOptions, correction) {
  try {
    const retry = await ClaudeService.sendStructuredMessage({ ...callOptions, system: callOptions.system + correction });
    return retry?.success ? retry.data : null;
  } catch {
    return null;
  }
}

/**
 * Two ways a reply can say a desk change happened when nothing was sent:
 * - desk_action is "append"/"new_doc" but surface_updates has no desk item;
 * - the reply's words promise a draft or update, but desk_action is null.
 * Either way, ask once more for the missing desk write before replying. If it still isn't there,
 * the reply is marked `deskWriteUnsent` so the chat shows that nothing reached the desk.
 */
export async function withDeskItemRetry(callOptions, parsed, { activeSurface } = {}) {
  const missingItem = claimsDeskWrite(parsed) && !findDeskUpdate(parsed.surface_updates);
  const unsentPromise = promisesUnsentDeskWrite(parsed, { activeSurface });
  if (!missingItem && !unsentPromise) return parsed;

  if (process.env.NODE_ENV === "development") {
    console.warn(
      missingItem ? "[Lumen] desk_action without a desk item — asking again" : "[Lumen] reply promises a desk change but sent none — asking again",
      parsed
    );
  }

  const correction = missingItem
    ? `\n\nCORRECTION: Your previous reply to this message set desk_action to "${parsed.desk_action}" ` +
      "but surface_updates had no desk item, so nothing reached the desk. Reply again with the same intent " +
      'and include the desk item in surface_updates: surface "desk", type "document", title, content' +
      (parsed.desk_action === "append" ? ", and the doc_id of the document to add to." : ".")
    : "\n\nCORRECTION: Your previous reply to this message told the user a desk change was drafted or on its way, " +
      "but desk_action was null and surface_updates had no desk item, so nothing reached the desk. " +
      "If you are making the change, send it now in this reply: set desk_action and include the desk item " +
      '(surface "desk", type "document", title, content, and doc_id when adding to a document). ' +
      "If you are not making a change, reword chat_response so it does not say anything was drafted, sent, added, or ready.";

  const retry = await askAgain(callOptions, correction);
  if (retry && claimsDeskWrite(retry) && findDeskUpdate(retry.surface_updates)) return retry;
  // The model chose not to write and no longer claims it did — that reply is honest as it stands.
  if (retry && unsentPromise && !claimsDeskWrite(retry) && !promisesUnsentDeskWrite(retry, { activeSurface })) return retry;
  return { ...parsed, deskWriteUnsent: true };
}
